import { getDealerSelect } from '@/api/get/config/get-company-select';
import getUserRolesSelect from '@/api/get/config/get-user-role-select';
import checkUserArchivedStatus from '@/api/post/check-user-archived';
import reinstateUser from '@/api/post/reinstate-user';
import { PrimaryOutlineButton } from '@/components/button/button-component';
import Card from '@/components/card/card-component';
import CircleIndicator from '@/components/circle-indicator/circle-indicator-component';
import { Col, Row } from '@/components/grid/grid-component';
import Loader from '@/components/loader/loader-component';
import Input from '@/components/react-hook-form-inputs/input/input-component';
import PhoneInputComponent from '@/components/react-hook-form-inputs/input/phone-input/phone-input-component';
import CreatableSelect from '@/components/react-hook-form-inputs/select-createable/creatable-search-component';
import SelectSearch from '@/components/react-hook-form-inputs/select-search/select-search-component';
import { useMutation } from '@/hooks/use-api';
import { Heading3Text } from '@/utils/global-fonts';
import {
  cellphoneNumberRegex,
  emailRegex,
  passwordRules,
  requiredValidationDropdownMessage,
  requiredValidationMessage,
} from '@/utils/validations';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FiX } from 'react-icons/fi';
import { MdLockOutline } from 'react-icons/md';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

const styledFormRow = { marginTop: '24px', marginBottom: '40px' };

/**
 * user form
 * @param { string }type - form type edit or create
 * @returns {JSX.Element}
 */
const UserForm = ({ type, isLoading: updateLoading, resendEmail, userId }) => {
  const [
    checkUserArchivedStatusFunc,
    { loading: checkUserArchivedStatusLoading },
  ] = useMutation(checkUserArchivedStatus);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [userStatus, setUserStatus] = useState('');
  const navigate = useNavigate();
  const { state } = useLocation();

  const [roleOptionsLoading, setRoleOptionsLoading] = useState(true);
  const [roleOptions, setRoleOptions] = useState([]);
  const [dealersLoading, setDealersLoading] = useState(true);
  const [dealerOptions, setDealerOptions] = useState([]);

  useEffect(() => {
    getUserRolesSelect().then((response) => {
      const roles = response.data.map((role) => ({
        value: role.id,
        label: role.name,
      }));
      setRoleOptions(roles);
      setRoleOptionsLoading(false);
    });
    getDealerSelect().then((response) => {
      const dealers = response.data.map((dealer) => ({
        value: dealer.id,
        label: dealer.name,
      }));
      setDealerOptions(dealers);
      setDealersLoading(false);
    });
  }, []);

  const {
    formState: { isLoading: formLoading, errors },
    watch,
    setValue,
    setError,
  } = useFormContext();

  const isLoading = formLoading || updateLoading;
  const userStatusInitialColor = watch('status');
  const userType = watch('user_type');

  const userStatusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'new', label: 'New' },
    ...(type !== 'create' ? [{ value: 'suspended', label: 'Suspended' }] : []),
  ];

  const userAccountOptions = [
    { value: 'user', label: 'User' },
    { value: 'company', label: 'Company' },
  ];

  const companyUserTypeOptions = [
    { value: 'general', label: 'General' },
    { value: 'admin', label: 'Admin' },
  ];

  useEffect(() => {
    if (state?.companyId) {
      setValue('user_type', 'company');
      setValue('company_id', state?.companyId);
    }
  }, [state?.companyId, setValue]);

  if (isLoading) {
    return <Loader loading />;
  }

  return (
    <Card padding="12px 16px">
      <Heading3Text className="title">
        {type === 'edit' ? 'Edit User' : 'Create User'}
      </Heading3Text>
      <Row>
        <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
          <SelectSearch
            name="user_type"
            cy="selUserType"
            placeholder="Please select a user type"
            label="Size"
            isLoading={isLoading}
            header="User Type"
            options={userAccountOptions}
            required={{
              required: 'Please select a user type',
              onChange: (e) => {
                if (e.target.value === 'company') {
                  setValue('company_type', 'general');
                }
              },
            }}
          />
        </Col>
        {userType === 'company' && (
          <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
            <SelectSearch
              name="company_type"
              cy="selCompanyType"
              placeholder="Please select a company user type"
              label="Size"
              isLoading={isLoading}
              header="Company User Type"
              options={companyUserTypeOptions}
              required={{
                required: 'Please select a company user type',
              }}
            />
          </Col>
        )}
        <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
          <SelectSearch
            header="User Role"
            placeholder="Please select a user role"
            options={roleOptions}
            name="user_role_id"
            cy="selUserRoles"
            isLoading={isLoading || roleOptionsLoading}
          />
        </Col>
        <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
          <Input
            disabled={isLoading}
            label="First Name"
            placeholder="First Name"
            name="first_name"
            registerOptions={{
              required: requiredValidationMessage,
            }}
          />
        </Col>
        <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
          <Input
            label="Last Name"
            disabled={isLoading}
            placeholder="Last Name"
            name="last_name"
            registerOptions={{
              required: requiredValidationMessage,
            }}
          />
        </Col>
        <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
          <Input
            label="Email Address"
            disabled={isLoading || checkUserArchivedStatusLoading}
            placeholder="Email Address"
            name="email"
            registerOptions={{
              validate: (value) => {
                let errorMessage = null;
                emailRegex.forEach((rule) => {
                  if (!rule.pattern.test(value)) {
                    errorMessage = rule.message;
                  }
                });
                return errorMessage || true;
              },
              onBlur: (x) =>
                !errors.email &&
                checkUserArchivedStatusFunc({ email: x.target.value }).then(
                  (res) => {
                    if (res.data.is_archived !== undefined) {
                      if (!res.data.is_archived && userId !== res.data.id) {
                        setError('email', { message: 'email already in use' });
                      } else if (res.data.is_archived) {
                        Swal.fire({
                          didOpen: (modal) => {
                            modal.setAttribute('id', 'confirm-reinstate-user');
                          },
                          title: 'Reinstate User',
                          text: 'A user with that email already exists. Do you want to reinstate them?',
                          showCancelButton: true,
                          confirmButtonText: 'Reinstate User',
                          showLoaderOnConfirm: true,
                          preConfirm: () => reinstateUser(res.data.id),
                        }).then(({ isConfirmed }) => {
                          if (isConfirmed) {
                            Swal.fire({
                              didOpen: (modal) => {
                                modal.setAttribute('id', 'reinstated-user');
                              },
                              title: 'Success!',
                              text: 'User has been reinstated',
                            }).then(() =>
                              navigate('/app/user-management/users'),
                            );
                          } else {
                            Notification.error({
                              title: 'Failed to reinstate user',
                              message: 'Error reinstating user',
                            });
                          }
                        });
                      }
                    }
                  },
                ),
            }}
          />
        </Col>
        {userType === 'company' && (
          <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
            <CreatableSelect
              header="Company"
              placeholder="Please select company name"
              options={dealerOptions}
              name="company_id"
              cy="selCompanyName"
              required={{
                required: 'please select a company name',
              }}
              onCreateOption={() => {
                navigate('/app/company-management/new', {
                  state: { userForm: true },
                });
              }}
              isLoading={isLoading || dealersLoading}
            />
          </Col>
        )}
        <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
          <PhoneInputComponent
            name="contact_number"
            disabled={isLoading}
            registerOptions={{
              required: requiredValidationMessage,
              validate: (value) => {
                let errorMessage = null;
                cellphoneNumberRegex.forEach((rule) => {
                  if (!rule.pattern.test(value)) {
                    errorMessage = rule.message;
                  }
                });
                return errorMessage || true;
              },
            }}
          />
        </Col>
        <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
          <Input
            disabled={isLoading}
            label="User Position"
            placeholder="User Position"
            name="position"
            registerOptions={{
              required: requiredValidationMessage,
            }}
          />
        </Col>
        <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
          <SelectSearch
            cy="selUserStatus"
            header="Status"
            name="status"
            label="Please select a status"
            isDisabled={type !== 'edit'}
            options={userStatusOptions}
            icon={
              <CircleIndicator status={userStatus || userStatusInitialColor} />
            }
            required={{
              required: requiredValidationDropdownMessage,
              onChange: (e) => {
                setUserStatus(e.target.value);
              },
            }}
            isLoading={isLoading}
          />
        </Col>
      </Row>
      {type === 'edit' && (
        <Row style={styledFormRow}>
          {showResetPassword ? (
            <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
              <Input
                iconLeft={<MdLockOutline />}
                label="Password"
                disabled={isLoading}
                placeholder="password"
                name="password"
                registerOptions={{
                  validate: (value) => {
                    let errorMessage = null;
                    passwordRules.forEach((rule) => {
                      if (!rule.pattern.test(value)) {
                        errorMessage = rule.message;
                      }
                    });
                    return errorMessage || true;
                  },
                }}
                type="password"
              />
              <PrimaryOutlineButton
                buttonWidth="fit-content"
                margin="0  0 0 120px"
                type="button"
                icon={<FiX />}
                loading={false}
                title="Cancel"
                onClick={() => {
                  setShowResetPassword(!showResetPassword);
                }}
              />
            </Col>
          ) : (
            <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
              <PrimaryOutlineButton
                buttonWidth="fit-content"
                type="button"
                loading={false}
                title="Reset User Password"
                onClick={() => {
                  setShowResetPassword(!showResetPassword);
                }}
              />
            </Col>
          )}
          {resendEmail && (
            <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
              <PrimaryOutlineButton
                buttonWidth="fit-content"
                type="button"
                loading={false}
                title="Resend Register Email"
                onClick={resendEmail}
              />
            </Col>
          )}
        </Row>
      )}
    </Card>
  );
};

UserForm.defaultProps = {
  isLoading: false,
  resendEmail: undefined,
  userId: null,
};

UserForm.propTypes = {
  type: PropTypes.string.isRequired,
  resendEmail: PropTypes.func,
  isLoading: PropTypes.bool,
  userId: PropTypes.string,
};

export default UserForm;
